// ============================================================================
// AGM Notice — agenda + checklist rules (ADR-0024)
// ============================================================================
// Pure helpers behind the AGM Notice editor: the printed numbering (ordinary
// business first, then special — as AgmPackModel.BuildAgenda prints it),
// moving an item within its group, the "suggest from Corporate Facts" agenda,
// and the pre-print checklist. No API / TipTap imports, so jest runs them.

import type {
  AgmLibraryEntry,
  AgmNotice,
  AgmResolution,
  CorporateFactsData,
} from 'api/types/fsa/corporateFacts.types';

/** Ordinary business then special business, each in saved order — the printed order. */
export function printedOrder(list: AgmResolution[]): AgmResolution[] {
  return [...list.filter(r => r.business !== 'SPECIAL'), ...list.filter(r => r.business === 'SPECIAL')];
}

/** Printed item number per resolution. */
export function agendaNumbers(list: AgmResolution[]): Map<string, number> {
  const map = new Map<string, number>();
  printedOrder(list).forEach((r, i) => map.set(r.local_id, i + 1));
  return map;
}

/** Swaps an item with its neighbour IN THE SAME BUSINESS GROUP (what the auditor sees move). */
export function moveWithinGroup(list: AgmResolution[], id: string, delta: -1 | 1): AgmResolution[] {
  const i = list.findIndex(r => r.local_id === id);
  if (i < 0) return list;
  const special = list[i].business === 'SPECIAL';
  let j = i + delta;
  while (j >= 0 && j < list.length && (list[j].business === 'SPECIAL') !== special) j += delta;
  if (j < 0 || j >= list.length) return list;
  const next = list.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export interface PrefillContext {
  auditFirmName?: string | null;
  auditFirmRegistrationNumber?: string | null;
}

/** Params pre-filled from facts already entered (dividends, director changes, the audit firm). */
export function prefillParams(entry: AgmLibraryEntry, data: CorporateFactsData, agenda: AgmResolution[], ctx: PrefillContext): Record<string, string> {
  switch (entry.code) {
    case 'FINAL_DIVIDEND': {
      const fin = (data.dividends ?? []).find(x => x.kind === 'FINAL');
      return fin ? {
        rate: fin.rate_percent != null ? String(fin.rate_percent) : '',
        per_share: fin.per_share_amount != null ? `Rs. ${fin.per_share_amount}` : '',
      } : {};
    }
    case 'AUDITOR_APPOINTMENT':
      return { auditor_firm: ctx.auditFirmName ?? '', frn: ctx.auditFirmRegistrationNumber ?? '' };
    case 'REGULARISE_ADDITIONAL_DIRECTOR': {
      const taken = new Set(agenda.filter(r => r.library_code === entry.code).map(r => r.params.din));
      const ch = (data.director_changes ?? []).find(c => c.kind === 'ADDITIONAL_DIRECTOR' && !taken.has(c.din ?? ''));
      return ch ? { director_name: ch.name, din: ch.din ?? '', appointment_date: ch.effective_date ?? '' } : {};
    }
    default:
      return {};
  }
}

/** A new agenda item — the library text COPIED (saved-as-primary). */
export function makeResolution(
  entry: AgmLibraryEntry, libraryVersion: number, data: CorporateFactsData,
  agenda: AgmResolution[], ctx: PrefillContext, newId: () => string,
): AgmResolution {
  return {
    local_id: newId(),
    library_code: entry.code,
    library_version: libraryVersion,
    title: entry.title,
    business: entry.business ?? 'ORDINARY',
    resolution_type: entry.resolution_type ?? 'ORDINARY',
    params: prefillParams(entry, data, agenda, ctx),
    param_specs: entry.params ?? [],
    proxy_label: entry.proxy_label ?? entry.title,
    text: entry.text,
    explanatory: entry.explanatory,
  };
}

/**
 * "Suggest from Corporate Facts": adoption of accounts, the final dividend if
 * one is recorded, and regularisation of each additional director recorded
 * in the year — only what is not on the agenda yet.
 */
export function suggestAgenda(
  library: { version: number; entries: AgmLibraryEntry[] }, data: CorporateFactsData,
  agenda: AgmResolution[], ctx: PrefillContext, newId: () => string,
): AgmResolution[] {
  const byCode = (c: string) => library.entries.find(e => e.kind === 'resolution' && e.code === c);
  const have = new Set(agenda.map(r => r.library_code));
  const out = [...agenda];
  const push = (c: string) => { const e = byCode(c); if (e) out.push(makeResolution(e, library.version, data, out, ctx, newId)); };
  if (!have.has('ADOPT_ACCOUNTS')) push('ADOPT_ACCOUNTS');
  if (!have.has('FINAL_DIVIDEND') && (data.dividends ?? []).some(x => x.kind === 'FINAL')) push('FINAL_DIVIDEND');
  const regularised = new Set(agenda.filter(r => r.library_code === 'REGULARISE_ADDITIONAL_DIRECTOR').map(r => r.params.din));
  const reg = byCode('REGULARISE_ADDITIONAL_DIRECTOR');
  if (reg) {
    for (const ch of (data.director_changes ?? []).filter(c => c.kind === 'ADDITIONAL_DIRECTOR' && !regularised.has(c.din ?? ''))) {
      out.push({
        ...makeResolution(reg, library.version, data, out, ctx, newId),
        params: { director_name: ch.name, din: ch.din ?? '', appointment_date: ch.effective_date ?? '' },
      });
    }
  }
  return out;
}

/** True when a TipTap document holds any visible text (or a field chip / table). */
export function docHasText(doc: unknown): boolean {
  if (!doc || typeof doc !== 'object') return false;
  const node = doc as { type?: string; text?: string; content?: unknown[] };
  if (node.type === 'text') return !!node.text?.trim();
  if (node.type === 'fieldChip' || node.type === 'dataTable') return true;
  return Array.isArray(node.content) && node.content.some(docHasText);
}

/** Days from one yyyy-MM-dd date to another (UTC, so no timezone drift). */
function daysBetween(from: string, to: string): number | null {
  const a = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(a) || Number.isNaN(b) ? null : Math.round((b - a) / 86_400_000);
}

export interface AgmCheck {
  id: string;
  label: string;
  ok: boolean;
  /** 'block' = the document prints with a gap; 'warn' = review. */
  level: 'block' | 'warn';
  hint?: string;
  /** Where to fix it — an editor item id ('details', 'intro', 'notes', a resolution id) or 'facts'. */
  target?: string;
}

export interface AgmFactsColumns {
  agmNumber?: number | null;
  agmDate?: string | null;
  agmVenue?: string | null;
}

/** The pre-print checklist. Only items that are NOT ok count as open. */
export function agmChecklist(notice: AgmNotice, agenda: AgmResolution[], agm: AgmFactsColumns): AgmCheck[] {
  const checks: AgmCheck[] = [];
  const meetingSet = !!(agm.agmNumber && agm.agmDate && agm.agmVenue);
  checks.push({
    id: 'meeting', level: 'block', ok: meetingSet, target: 'facts',
    label: 'AGM number, date and venue set in Corporate Facts',
    hint: meetingSet ? undefined : 'The Notice, proxy form and attendance slip print them.',
  });
  checks.push({ id: 'notice_date', level: 'block', ok: !!notice.notice_date, target: 'details', label: 'Date of the Notice' });
  if (notice.notice_date && agm.agmDate) {
    // s.101(1): at least 21 CLEAR days — the day of the notice and of the meeting excluded.
    const gap = daysBetween(notice.notice_date, agm.agmDate);
    const clear = gap == null ? null : gap - 1;
    checks.push({
      id: 'clear_days', level: 'warn', ok: clear != null && clear >= 21, target: 'details',
      label: 'At least 21 clear days’ notice (s.101)',
      hint: clear == null ? undefined
        : clear < 0 ? 'The Notice is dated after the meeting.'
          : clear >= 21 ? `${clear} clear days.`
            : `${clear} clear day(s) — a shorter notice needs the members’ consent.`,
    });
  }
  checks.push({
    id: 'signatory', level: 'block', ok: !!notice.signatory?.name?.trim(), target: 'details',
    label: 'Signed by (By order of the Board)',
  });
  checks.push({ id: 'intro', level: 'block', ok: docHasText(notice.intro), target: 'intro', label: 'Introduction' });
  checks.push({
    id: 'agenda', level: 'block', ok: agenda.length > 0, target: agenda[0]?.local_id,
    label: 'At least one item of business',
  });
  if (agenda.length > 0) {
    checks.push({
      id: 'adopt', level: 'warn', ok: agenda.some(r => r.library_code === 'ADOPT_ACCOUNTS'),
      label: 'Adoption of the financial statements on the agenda',
    });
  }
  const numbers = agendaNumbers(agenda);
  for (const r of printedOrder(agenda)) {
    const n = numbers.get(r.local_id);
    const missing = (r.param_specs ?? []).filter(ps => ps.required && !(r.params[ps.key] ?? '').trim());
    if (missing.length) {
      checks.push({
        id: `params:${r.local_id}`, level: 'block', ok: false, target: r.local_id,
        label: `Item ${n}: ${missing.map(ps => ps.label).join(', ')}`, hint: r.title,
      });
    }
    if (!docHasText(r.text)) {
      checks.push({ id: `text:${r.local_id}`, level: 'block', ok: false, target: r.local_id, label: `Item ${n}: resolution text`, hint: r.title });
    }
    if (r.business === 'SPECIAL' && !docHasText(r.explanatory)) {
      checks.push({
        id: `expl:${r.local_id}`, level: 'block', ok: false, target: r.local_id,
        label: `Item ${n}: explanatory statement (s.102)`, hint: r.title,
      });
    }
  }
  return checks;
}
